import * as React from "react";
import {debounce} from "throttle-debounce";
import KEY from "/src/constants/keyboard-keydown-key";
import {css} from "/src/utils/utils";
import translate from "/src/utils/translate";
import Icon, {IconSVG} from "../../icon/Icon";
import ChevronRightIcon from "/src/svg/chevron-right.svg";

import * as styles from "./navigation-dropdown.module.scss";

interface Props {
  children: (focusedListItem: number) => React.ReactNode;
  label: string;
  onSelect?: (index: number) => void;
  numberOfItems: number;
  leftIconSvg?: IconSVG;
}

export default function NavigationDropdown(props: Props): React.ReactElement {
  const [focusedListItem, setFocusedListItem] = React.useState<number>(-1);
  const [expanded, setExpanded] = React.useState<boolean>(false);

  const hovering = React.useRef<boolean>(false);

  const {children, label, leftIconSvg, onSelect, numberOfItems} = props;

  const onExpandToggle = React.useCallback(() => {
    setExpanded(!expanded);
  }, [expanded]);

  const debouncedSetExpanded = debounce(375, () => {
    if (!hovering.current) {
      setExpanded(false);
    }
  });

  const handleOnMouseEnterDropdown = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      (event.target as HTMLDivElement).blur();
      hovering.current = true;
      setFocusedListItem(-1);
    },
    []
  );

  const handleOnMouseEnterButton = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      (event.target as HTMLButtonElement).blur();
      setExpanded(true);
      hovering.current = true;
      setFocusedListItem(-1);
    },
    []
  );

  const handleOnMouseLeaveDropdown = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      (event.target as HTMLDivElement).blur();
      setExpanded(false);
      hovering.current = false;
      setFocusedListItem(-1);
      debouncedSetExpanded();
    },
    [debouncedSetExpanded]
  );

  const handleOnMouseLeaveButton = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      (event.target as HTMLButtonElement).blur();
      hovering.current = false;
      setFocusedListItem(-1);
      debouncedSetExpanded();
    },
    [debouncedSetExpanded]
  );

  const onKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      switch (event.key) {
        case KEY.UP: {
          event.preventDefault();
          const tempIdx = focusedListItem > 0 ? focusedListItem - 1 : 0;
          setFocusedListItem(tempIdx);
          break;
        }
        case KEY.DOWN: {
          event.preventDefault();
          const tempIdx =
            focusedListItem < numberOfItems ? focusedListItem + 1 : 0;
          setFocusedListItem(tempIdx);
          break;
        }
        case KEY.HOME:
          event.preventDefault();
          setFocusedListItem(0);
          break;
        case KEY.END:
          event.preventDefault();
          setFocusedListItem(numberOfItems - 1);
          break;
        case KEY.ESC:
          event.preventDefault();
          setExpanded(false);
          setFocusedListItem(-1);
          break;
        case KEY.SPACE:
        case KEY.ENTER:
          event.preventDefault();
          if (event.key === KEY.ENTER && focusedListItem !== -1) {
            if (onSelect) {
              onSelect(focusedListItem);
            }
            setExpanded(false);
            setFocusedListItem(-1);
            return;
          }

          setExpanded(true);
          break;
        default:
          break;
      }
    },
    [numberOfItems, onSelect, focusedListItem]
  );

  return (
    <div
      className={css(
        styles.navigationDropdown,
        expanded ? styles.navigationDropdownExpanded : ""
      )}
    >
      <div className={styles.navigationDropdown__btnWrapper}>
        <button
          className={css(styles.navigationDropdown__btn)}
          type="button"
          aria-haspopup="listbox"
          aria-label={translate("Language")}
          aria-expanded={expanded}
          tabIndex={0}
          onClick={onExpandToggle}
          onMouseEnter={handleOnMouseEnterButton}
          onMouseLeave={handleOnMouseLeaveButton}
          onKeyDown={onKeyDown}
        >
          {leftIconSvg && (
            <Icon
              role="presentation"
              classes={styles.navigationDropdown__leftIcon}
              svg={leftIconSvg}
            />
          )}
          {label}
          <Icon
            role="presentation"
            classes={styles.navigationDropdown__rightIcon}
            svg={ChevronRightIcon}
          />
        </button>
      </div>

      <div
        className={css(styles.navigationDropdown__itemsWrapper)}
        onMouseEnter={handleOnMouseEnterDropdown}
        onMouseLeave={handleOnMouseLeaveDropdown}
      >
        <div className={css(styles.navigationDropdown__itemsCaret)} />
        <ul
          className={css(styles.navigationDropdown__items)}
          role="listbox"
          aria-label="Language"
          tabIndex={expanded ? 0 : -1}
        >
          {children(focusedListItem)}
        </ul>
      </div>
    </div>
  );
}
